import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Bot, 
  Fingerprint,
  Check,
  ArrowRight,
  MessageCircle,
  Star
} from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { PrimaryCTA } from '@/components/PrimaryCTA';
import { Breadcrumbs } from '@/components/Breadcrumbs';

// Unfair Advantage Tech Stack
const techStack = [
  {
    id: 'speed',
    icon: Zap,
    title: 'React Speed Engine',
    description: "We don't use WordPress. We use the same tech as Netflix (Next.js) for instant 0.5s load times.",
    gradient: 'from-yellow-500/20 to-orange-500/20',
    iconColor: 'text-yellow-400',
  },
  {
    id: 'ai',
    icon: Bot,
    title: 'AI-Ready Infrastructure',
    description: 'Future-proof. We code with Advanced Schema Markup so ChatGPT, Gemini, and Siri recommend you.',
    gradient: 'from-purple-500/20 to-pink-500/20',
    iconColor: 'text-purple-400',
  },
  {
    id: 'conversion',
    icon: Fingerprint,
    title: 'One-Touch Conversion',
    description: 'Sticky mobile buttons for One-Tap Calling & WhatsApp. No friction.',
    gradient: 'from-teal-500/20 to-cyan-500/20',
    iconColor: 'text-teal-400',
  },
];

// Pricing Packages
const pricingPlans = [
  {
    id: 'essentials',
    name: 'The Essentials',
    price: '695',
    tagline: 'Perfect for Startups.',
    features: [
      'High-Speed Single Page',
      'One-Tap Mobile Buttons',
      'Basic Schema Markup',
      'Domain Setup',
    ],
    isPopular: false,
    cta: 'Get Started',
    link: '/contact?package=essentials',
  },
  {
    id: 'growth',
    name: 'The Growth Engine',
    price: '1,195',
    tagline: 'Best for Established Trades.',
    features: [
      '5-Page High-Performance Build',
      'Advanced AI & Local Schema',
      'Done-For-You Copywriting',
      'Google Maps Optimisation',
    ],
    isPopular: true,
    cta: 'Get Growth Engine',
    link: '/contact?package=growth',
  },
  {
    id: 'leader',
    name: 'Market Leader',
    price: '2,495',
    tagline: 'For Regional Dominance.',
    features: [
      '10+ Pages',
      'Location Silos (e.g. Plumber Cardiff)',
      'Competitor Gap Analysis',
      'Priority Support',
    ],
    isPopular: false,
    cta: 'Dominate Your Market',
    link: '/contact?package=leader',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

// SEO Meta
const pageTitle = "Web Design & SEO Services | SmartSite Solutions";
const pageDescription = "One-time packages from £695: lightning-fast React websites, AI-ready schema, one-touch conversion buttons and local SEO for South Wales trades. No monthly fees.";

export default function Services() {
  // Set page title and meta
  useEffect(() => {
    // Add Service schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "SmartSite Solutions Pricing Packages",
      "description": pageDescription,
      "numberOfItems": pricingPlans.length,
      "itemListElement": pricingPlans.map((plan, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": plan.name,
          "description": plan.tagline,
          "offers": {
            "@type": "Offer",
            "url": "https://www.smartsitesolutions.co.uk/services",
            "price": plan.price.replace(',', ''),
            "priceCurrency": "GBP",
            "availability": "https://schema.org/InStock"
          },
          "image": "https://www.smartsitesolutions.co.uk/og-image.png",
          "provider": {
            "@type": "LocalBusiness",
            "name": "SmartSite Solutions",
            "telephone": "+447852872088",
            "email": "smartsitesolutions@pm.me",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "North Cornelly",
              "addressLocality": "Bridgend",
              "addressRegion": "South Wales",
              "postalCode": "CF33",
              "addressCountry": "GB"
            }
          }
        }
      }))
    };

    let scriptTag = document.querySelector('script[data-schema="services"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'services');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, []);

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/services" />
      </Helmet>
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <Breadcrumbs items={breadcrumbItems} />

          {/* Hero Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto mb-20"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-muted-foreground mb-6">
              Services & Pricing
            </span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold mb-6">
              Services That{' '}
              <span className="text-gradient">Pay For Themselves</span>
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Most websites are expenses. Ours are investments. We combine high-speed engineering 
              with AI-ready infrastructure to turn traffic into revenue.
            </p>
          </motion.div>

          {/* Unfair Advantage Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl lg:text-3xl font-bold mb-3">
                The Unfair Advantage
              </h2>
              <p className="text-muted-foreground">
                Why our clients outperform the competition
              </p>
            </div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
            >
              {techStack.map((tech) => (
                <motion.div
                  key={tech.id}
                  variants={cardVariants}
                  className="glass-card p-6 md:p-8 text-center hover:border-white/20 transition-all duration-300 group"
                >
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tech.gradient} flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <tech.icon className={`w-8 h-8 ${tech.iconColor}`} />
                  </div>

                  {/* Title */}
                  <h3 className="font-display text-xl font-bold mb-3 group-hover:text-gradient transition-all duration-300">
                    {tech.title}
                  </h3>

                  {/* Description */}
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {tech.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.section>

          {/* Pricing Packages Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl lg:text-3xl font-bold mb-3">
                Choose Your Package
              </h2>
              <p className="text-muted-foreground">
                Transparent pricing. No hidden fees. Pay once, own forever.
              </p>
            </div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8"
            >
              {pricingPlans.map((plan) => (
                <motion.div
                  key={plan.id}
                  variants={cardVariants}
                  className={`relative group ${plan.isPopular ? 'md:-mt-4 md:mb-4' : ''}`}
                >
                  {/* Popular Badge */}
                  {plan.isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold shadow-lg shadow-orange-500/30">
                        <Star className="w-4 h-4 fill-current" />
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className={`glass-card h-full p-6 md:p-8 transition-all duration-500 hover:border-white/20 flex flex-col ${plan.isPopular ? 'border-orange-500/30 bg-white/10 shadow-xl shadow-orange-500/10' : ''}`}>
                    {/* Plan Name & Tagline */}
                    <div className="text-center mb-6">
                      <h3 className="font-display text-xl font-bold mb-2">
                        {plan.name}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {plan.tagline}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="text-center mb-8">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-2xl font-semibold text-muted-foreground">£</span>
                        <span className={`font-display text-5xl font-bold ${plan.isPopular ? 'text-gradient' : ''}`}>
                          {plan.price}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">One-time payment</p>
                    </div>

                    {/* Features */}
                    <ul className="space-y-4 mb-8 flex-grow">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <Check className={`w-5 h-5 shrink-0 mt-0.5 ${plan.isPopular ? 'text-orange-400' : 'text-teal-400'}`} />
                          <span className="text-sm text-foreground/80">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <Button 
                      asChild
                      className="w-full primary-cta h-14 text-base group/btn"
                    >
                      <a 
                        href="https://calendly.com/smartsitesolutions-pm/30min" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Book Free Strategy Call
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.section>

          {/* Care Plan Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="glass-card p-6 md:p-8 lg:p-12 bg-gradient-to-br from-teal-500/10 via-transparent to-cyan-500/10 border-teal-500/20">
              <div className="max-w-3xl mx-auto text-center">
                {/* Badge */}
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/20 border border-teal-500/30 text-teal-400 text-sm font-medium mb-6">
                  <MessageCircle className="w-4 h-4" />
                  Ongoing Support
                </span>

                {/* Headline */}
                <h2 className="font-display text-3xl lg:text-4xl font-bold mb-4">
                  The SmartSite Care Plan
                </h2>

                {/* Price */}
                <div className="flex items-baseline justify-center gap-2 mb-6">
                  <span className="font-display text-4xl lg:text-5xl font-bold text-gradient">
                    £39
                  </span>
                  <span className="text-xl text-muted-foreground">/ month</span>
                  <span className="ml-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-muted-foreground">
                    No contracts
                  </span>
                </div>

                {/* Description */}
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  A website needs oiling just like a van. Includes Hosting, Backups, Security, 
                  and Unlimited Small Edits via WhatsApp.
                </p>

                {/* Features Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-10">
                  {['Premium Hosting', 'Daily Backups', 'Security Updates', 'Unlimited Edits'].map((feature) => (
                    <div key={feature} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                      <Check className="w-4 h-4 text-teal-400" />
                      <span className="text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <PrimaryCTA 
                  text="Add Care Plan"
                  size="lg"
                />
              </div>
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
